/**
 * Pure geometry helpers for the Circle-of-Fifths hit-test. Extracted from
 * `circle-of-fifths.tsx` so the angle-to-slot math is unit-testable without a canvas.
 *
 * The wheel uses the same conventions as `draw-circle.ts`:
 *   - 12 slots indexed i ∈ [1, 12]
 *   - Slot i has midAngle = (i - 1) * rad, where rad = 2π/12
 *   - Slot i maps to data via index (i + 3 - 1) % 12 (places C major at the top)
 *
 * Inverting the draw geometry: i = round(theta / rad) + 1, with theta normalized to [0, 2π).
 */

const TWO_PI = 2 * Math.PI
const RAD = TWO_PI / 12

/**
 * Convert a normalized canvas angle (0 ≤ theta < 2π) to a wheel slot i ∈ [1, 12].
 */
export const angleToSlot = (theta: number): number => {
  const normalized = ((theta % TWO_PI) + TWO_PI) % TWO_PI
  return (Math.round(normalized / RAD) % 12) + 1
}

/**
 * Hit-test a canvas-space (x, y) against the CoF wheel laid out at (width, height).
 * Returns `{ slotIndex, ring }` for the slot under the cursor, or null if outside
 * the wheel or inside the center hole.
 *
 * `slotIndex` is the index into the `ComplementaryScales[]` array passed to
 * `drawCircleOfFifths`, i.e. `(i + 3 - 1) % 12` where i is the visual slot.
 */
export const hitTestCof = (
  x: number,
  y: number,
  width: number,
  height: number
): { slotIndex: number; ring: 'major' | 'minor' } | null => {
  const padding = 16
  const innerWidth = width - 2 * padding
  if (innerWidth <= 0) {
    return null
  }
  const radius = innerWidth / 2
  const innerRadius = innerWidth / 3
  const centerX = width / 2
  const centerY = height / 2
  const dx = x - centerX
  const dy = y - centerY
  const dist = Math.sqrt(dx * dx + dy * dy)
  if (dist > radius || dist < innerRadius / 2) {
    return null
  }
  const ring: 'major' | 'minor' = dist > innerRadius ? 'major' : 'minor'
  const i = angleToSlot(Math.atan2(dy, dx))
  const slotIndex = (i + 3 - 1) % 12
  return { slotIndex, ring }
}
